import React, { useState, useEffect, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  FileCheck, Upload, Shield, AlertTriangle, Phone, ExternalLink,
  CheckCircle, XCircle, FileText, Building2, Award, RefreshCw,
  Loader2, ArrowRight, MessageCircle, Lock, Info
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const WHATSAPP_NUMBER = "5521964894655";
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}`;

const Contabilidade = ({ userRole = "afiliado" }) => {
  const [certificadoFile, setCertificadoFile] = useState(null);
  const [certificadoStatus, setCertificadoStatus] = useState(null);
  const [cnpjStatus, setCnpjStatus] = useState(null);
  const [cnpj, setCnpj] = useState('');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    try {
      setLoading(true);
      const userId = userRole === 'fornecedor' ? 'fornecedor-001' : 'afiliado-demo-001';
      const response = await fetch(`${BACKEND_URL}/api/contabilidade/status/${userId}`);
      if (response.ok) {
        const data = await response.json();
        setCertificadoStatus(data.certificado);
        setCnpjStatus(data.cnpj);
        if (data.cnpj?.numero) setCnpj(data.cnpj.numero);
      }
    } catch (error) {
      console.error('Erro:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) {
      if (file.name.endsWith('.pfx') || file.name.endsWith('.p12') || file.name.endsWith('.cer')) {
        setCertificadoFile(file);
        toast.success(`Arquivo "${file.name}" selecionado`);
      } else {
        toast.error('Formato invalido. Use .pfx, .p12 ou .cer');
      }
    }
  }, []);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setCertificadoFile(file);
      toast.success(`Arquivo "${file.name}" selecionado`);
    }
  };

  const handleUploadCertificado = async () => {
    if (!certificadoFile) {
      toast.error('Selecione um arquivo de certificado digital');
      return;
    }
    try {
      setUploading(true);
      const userId = userRole === 'fornecedor' ? 'fornecedor-001' : 'afiliado-demo-001';
      const formData = new FormData();
      formData.append('file', certificadoFile);
      formData.append('user_id', userId);
      formData.append('user_type', userRole);

      const response = await fetch(`${BACKEND_URL}/api/contabilidade/certificado/upload`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        toast.success('Certificado digital enviado com sucesso!');
        setCertificadoFile(null);
        await loadStatus();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Erro ao enviar certificado');
      }
    } catch (error) {
      toast.error('Erro ao enviar certificado');
    } finally {
      setUploading(false);
    }
  };

  const handleSaveCnpj = async () => {
    if (!cnpj || cnpj.length < 14) {
      toast.error('Informe um CNPJ valido (14 digitos)');
      return;
    }
    try {
      setUploading(true);
      const userId = userRole === 'fornecedor' ? 'fornecedor-001' : 'afiliado-demo-001';
      const response = await fetch(`${BACKEND_URL}/api/contabilidade/cnpj`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, user_type: userRole, cnpj: cnpj.replace(/\D/g, '') })
      });
      if (response.ok) {
        toast.success('CNPJ cadastrado com sucesso!');
        await loadStatus();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Erro ao salvar CNPJ');
      }
    } catch (error) {
      toast.error('Erro ao salvar CNPJ');
    } finally {
      setUploading(false);
    }
  };

  const hasCertificado = certificadoStatus?.ativo;
  const hasCnpj = cnpjStatus?.ativo;
  const canSell = hasCertificado && hasCnpj;

  if (loading) {
    return <div className="flex items-center justify-center h-full"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div>;
  }

  return (
    <div className="p-4 lg:p-6 bg-gray-950 overflow-auto h-full">
      {/* Header */}
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-1">
          <img src="/images/clickhub-logo.png" alt="ClickHub" className="w-6 h-6 object-contain" />
          <span className="bg-gradient-to-r from-purple-500 to-violet-500 text-white text-xs font-bold px-2 py-1 rounded-full uppercase">Contabilidade</span>
          <h1 data-testid="contabilidade-title" className="text-xl font-bold text-white">Área Fiscal e Contábil</h1>
        </div>
        <p className="text-sm text-gray-400">Gerencie seu certificado digital e CNPJ para emissão de notas fiscais</p>
      </div>

      {/* Status Geral */}
      <Card className={`p-4 mb-4 border ${canSell ? 'bg-green-500/10 border-green-500/40' : 'bg-red-500/10 border-red-500/40'}`}>
        <div className="flex items-center gap-3">
          {canSell ? (
            <>
              <CheckCircle className="w-8 h-8 text-green-400" />
              <div>
                <h3 className="text-green-400 font-bold text-base">Documentação Completa</h3>
                <p className="text-green-300 text-sm">Voce esta habilitado para vender e emitir notas fiscais na plataforma.</p>
              </div>
            </>
          ) : (
            <>
              <AlertTriangle className="w-8 h-8 text-red-400" />
              <div>
                <h3 className="text-red-400 font-bold text-base">
                  {userRole === 'fornecedor' ? 'Acao Necessaria - Conta Inativa' : 'Acao Necessaria para Vender'}
                </h3>
                <p className="text-red-300 text-sm">
                  {userRole === 'fornecedor' 
                    ? 'Seu cadastro nao sera ativado na plataforma sem certificado digital e CNPJ.' 
                    : 'Voce pode navegar no sistema, mas nao pode vender sem CNPJ e certificado digital.'}
                </p>
              </div>
            </>
          )}
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* ===== CNPJ ===== */}
        <Card className="bg-gray-900/50 border-gray-700 p-5">
          <div className="flex items-center gap-2 mb-4">
            <Building2 className={`w-6 h-6 ${hasCnpj ? 'text-green-400' : 'text-orange-400'}`} />
            <h3 className="text-white font-bold text-base">CNPJ</h3>
            {hasCnpj && <span className="bg-green-500 text-white text-[10px] px-2 py-0.5 rounded-full ml-auto">Ativo</span>}
          </div>

          {hasCnpj ? (
            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 mb-3">
              <p className="text-green-400 text-sm font-semibold">CNPJ Cadastrado</p>
              <p className="text-white text-lg font-mono mt-1">{cnpjStatus?.numero?.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5') || cnpj}</p>
            </div>
          ) : (
            <>
              <div className="mb-3">
                <label className="block text-sm text-gray-300 mb-1">Informe seu CNPJ *</label>
                <Input
                  placeholder="00.000.000/0000-00"
                  value={cnpj}
                  onChange={(e) => setCnpj(e.target.value.replace(/\D/g, '').slice(0, 14))}
                  className="bg-gray-800 border-gray-700 font-mono"
                  data-testid="cnpj-input"
                />
              </div>
              <Button className="w-full bg-orange-500 hover:bg-orange-600 mb-3" onClick={handleSaveCnpj} disabled={uploading}>
                {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                Cadastrar CNPJ
              </Button>
            </>
          )}

          {/* Nao tenho CNPJ */}
          <Card className="bg-violet-500/10 border-violet-500/30 p-3">
            <p className="text-violet-400 font-semibold text-sm mb-1">Nao tenho CNPJ - Quero constituir um</p>
            <p className="text-gray-300 text-xs mb-2">
              Fale com nosso escritorio de contabilidade! Ajudamos voce a abrir seu CNPJ como MEI (Microempreendedor Individual) ou ME.
            </p>
            <a href={`${WHATSAPP_URL}?text=Ola! Sou ${userRole === 'fornecedor' ? 'fornecedor' : 'afiliado'} ClickHub e preciso constituir um CNPJ.`}
              target="_blank" rel="noopener noreferrer">
              <Button className="w-full bg-green-600 hover:bg-green-700 text-sm" data-testid="whatsapp-cnpj-btn">
                <MessageCircle className="w-4 h-4 mr-2" />
                WhatsApp: (21) 96489-4355
              </Button>
            </a>
          </Card>
        </Card>

        {/* ===== CERTIFICADO DIGITAL ===== */}
        <Card className="bg-gray-900/50 border-gray-700 p-5">
          <div className="flex items-center gap-2 mb-4">
            <Award className={`w-6 h-6 ${hasCertificado ? 'text-green-400' : 'text-orange-400'}`} />
            <h3 className="text-white font-bold text-base">Certificado Digital (e-CNPJ)</h3>
            {hasCertificado && <span className="bg-green-500 text-white text-[10px] px-2 py-0.5 rounded-full ml-auto">Ativo</span>}
          </div>

          {hasCertificado ? (
            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 mb-3">
              <p className="text-green-400 text-sm font-semibold">Certificado Valido</p>
              <p className="text-gray-300 text-xs mt-1">Arquivo: {certificadoStatus?.arquivo || 'certificado.pfx'}</p>
              <p className="text-gray-300 text-xs">Validade: {certificadoStatus?.validade || 'Ativo'}</p>
            </div>
          ) : (
            <>
              {/* Drop Zone */}
              <div
                className={`border-2 border-dashed rounded-lg p-6 text-center transition-all cursor-pointer mb-3 ${
                  dragOver ? 'border-orange-500 bg-orange-500/10' : 'border-gray-600 hover:border-gray-500'
                }`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => document.getElementById('cert-file-input').click()}
                data-testid="cert-dropzone"
              >
                <Upload className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-300 text-sm font-medium">
                  {certificadoFile ? certificadoFile.name : 'Arraste seu certificado digital aqui'}
                </p>
                <p className="text-gray-500 text-xs mt-1">
                  {certificadoFile ? `${(certificadoFile.size / 1024).toFixed(1)} KB` : 'Formatos: .pfx, .p12, .cer'}
                </p>
                <input
                  id="cert-file-input"
                  type="file"
                  accept=".pfx,.p12,.cer"
                  className="hidden"
                  onChange={handleFileSelect}
                />
              </div>

              {certificadoFile && (
                <Button className="w-full bg-orange-500 hover:bg-orange-600 mb-3" onClick={handleUploadCertificado} disabled={uploading}>
                  {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                  Enviar Certificado
                </Button>
              )}
            </>
          )}

          {/* Adquirir ou Renovar */}
          <Card className="bg-blue-500/10 border-blue-500/30 p-3 mb-3">
            <p className="text-blue-400 font-semibold text-sm mb-1">Nao tenho certificado digital</p>
            <p className="text-gray-300 text-xs mb-2">
              Adquira agora seu certificado digital e-CNPJ (A1 ou A3) ou faca a renovacao do seu certificado vencido.
            </p>
            <a href={`${WHATSAPP_URL}?text=Ola! Sou ${userRole === 'fornecedor' ? 'fornecedor' : 'afiliado'} ClickHub e preciso de um certificado digital.`}
              target="_blank" rel="noopener noreferrer">
              <Button className="w-full bg-green-600 hover:bg-green-700 text-sm" data-testid="whatsapp-cert-btn">
                <MessageCircle className="w-4 h-4 mr-2" />
                Adquirir/Renovar - WhatsApp: (21) 96489-4355
              </Button>
            </a>
          </Card>

          <Card className="bg-yellow-500/10 border-yellow-500/30 p-3">
            <p className="text-yellow-400 font-semibold text-sm mb-1">Renovação de Certificado</p>
            <p className="text-gray-300 text-xs">
              Se seu certificado esta vencido ou proximo do vencimento, renove agora para nao perder suas vendas.
            </p>
            <a href={`${WHATSAPP_URL}?text=Ola! Preciso renovar meu certificado digital.`}
              target="_blank" rel="noopener noreferrer" className="text-yellow-400 text-xs hover:underline mt-1 flex items-center gap-1">
              <Phone className="w-3 h-3" /> Falar sobre renovacao
            </a>
          </Card>
        </Card>
      </div>

      {/* Nota sobre NF-e */}
      <Card className="bg-gray-800/50 border-gray-700 p-4 mt-4">
        <div className="flex items-center gap-3">
          <FileText className="w-6 h-6 text-blue-400 flex-shrink-0" />
          <div>
            <h4 className="text-white font-semibold text-sm">Emissao de Nota Fiscal Automatica</h4>
            <p className="text-gray-400 text-xs mt-0.5">
              Quando a plataforma notificar uma venda, tanto o {userRole === 'fornecedor' ? 'fornecedor' : 'afiliado'} quanto o parceiro devem emitir NF-e.
              Com seu certificado digital cadastrado, o ClickHub facilita o processo de emissao direto pela plataforma.
            </p>
          </div>
        </div>
      </Card>

      {/* Bloqueio de vendas */}
      {!canSell && (
        <Card className="bg-red-500/10 border-red-500/40 p-4 mt-4">
          <div className="flex items-center gap-3">
            <Lock className="w-6 h-6 text-red-400 flex-shrink-0" />
            <div>
              <h4 className="text-red-400 font-semibold text-sm">Vendas Bloqueadas</h4>
              <p className="text-red-300 text-xs mt-0.5">
                {!hasCnpj && !hasCertificado && 'Cadastre seu CNPJ e envie seu certificado digital para desbloquear as vendas.'}
                {hasCnpj && !hasCertificado && 'Envie seu certificado digital para desbloquear as vendas.'}
                {!hasCnpj && hasCertificado && 'Cadastre seu CNPJ para desbloquear as vendas.'}
              </p>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

export default Contabilidade;
